import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '../lib/utils';
import { Upload, FileSpreadsheet, AlertTriangle, CheckCircle, RefreshCw, ArrowRight, Save, Trash2, PlayCircle, Loader2, Settings2, Pencil } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';

const CATEGORIES_INTERNES = [
  'Chiffre d\'Affaires',
  'Achats Alimentaires',
  'Charges Locatives',
  'Frais Bancaires',
  'Salaires & Charges',
  'Marketing & Publicité',
  'Télécommunications',
  'Énergie',
  'Assurances',
  'Impôts & Taxes',
  'Services Externes',
  'Équipements',
  'Frais de Livraison',
  'Divers',
];

const SOUS_CATEGORIES = [
  'TPE / CB', 'Espèces', 'Ticket Restaurant',
  'Deliveroo', 'Uber Eats', 'Delicity',
  'Matières Premières', 'Emballages',
  'Loyer', 'Charges Copro',
  'Électricité', 'Gaz', 'Internet', 'Téléphone',
  'Salaires', 'Charges Sociales', 'Mutuelle',
  'Frais CB', 'Frais Bancaires', 'Location TPE',
  'TVA', 'CFE', 'Pub Facebook', 'Flyers', 'Autre'
];

export function LinxoImportPage() {
  const { api } = useAuth();
  const [CATEGORIES_INTERNES, setCategoriesInternes] = useState([]);
  const [SOUS_CATEGORIES, setSousCategories] = useState([]);
  const [cashflow, setCashflow] = useState([]);
  const [nonCatGroupes, setNonCatGroupes] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  // Historique filters
  const [filterMois, setFilterMois] = useState('all');
  const [filterLibelle, setFilterLibelle] = useState('');
  const [filterCatBanque, setFilterCatBanque] = useState('all');
  const [filterCatInterne, setFilterCatInterne] = useState('all');
  const [filterType, setFilterType] = useState('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [cfRes, grpRes, mapRes, catRes] = await Promise.all([
        api.get('/cashflow'),
        api.get('/cashflow/non-categorises/groupes'),
        api.get('/mapping-linxo'),
        api.get('/categories'),
      ]);
      setCashflow(cfRes.data);
      setNonCatGroupes(grpRes.data);
      setMappings(mapRes.data);
      if (catRes.data.categories?.length > 0) {
        setCategoriesInternes(catRes.data.categories.map(c => c.nom));
      }
      if (catRes.data.sous_categories?.length > 0) {
        setSousCategories(catRes.data.sous_categories.map(c => c.nom));
      }
    } catch (error) {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ============ FILE PARSING ============
  const decodeBuffer = (buffer) => {
    const bytes = new Uint8Array(buffer);
    // UTF-16 LE BOM
    if (bytes.length >= 2 && bytes[0] === 0xFF && bytes[1] === 0xFE) {
      try { return new TextDecoder('utf-16le').decode(buffer); } catch(e) {}
    }
    // UTF-16 BE BOM
    if (bytes.length >= 2 && bytes[0] === 0xFE && bytes[1] === 0xFF) {
      try { return new TextDecoder('utf-16be').decode(buffer); } catch(e) {}
    }
    // UTF-8 BOM
    if (bytes.length >= 3 && bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF) {
      try { return new TextDecoder('utf-8').decode(buffer); } catch(e) {}
    }
    // Try UTF-8 first
    try {
      const text = new TextDecoder('utf-8', { fatal: true }).decode(buffer);
      return text;
    } catch(e) {}
    // Fallback: windows-1252 (superset of latin-1, common for French files)
    try { return new TextDecoder('windows-1252').decode(buffer); } catch(e) {}
    // Last resort
    return Array.from(bytes).map(b => String.fromCharCode(b)).join('');
  };

  const findColumn = (headers, ...candidates) => {
    const normalized = headers.map(h => h.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim());
    for (const candidate of candidates) {
      const c = candidate.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
      const idx = normalized.indexOf(c);
      if (idx !== -1) return idx;
    }
    // Partial match
    for (const candidate of candidates) {
      const c = candidate.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
      const idx = normalized.findIndex(h => h.includes(c) || c.includes(h));
      if (idx !== -1) return idx;
    }
    return -1;
  };

  const parseMontant = (str) => {
    if (!str) return 0;
    const cleaned = str.replace(/\xa0/g, '').replace(/\u00a0/g, '').replace(/\s/g, '').replace(',', '.');
    const val = parseFloat(cleaned);
    return isNaN(val) ? 0 : val;
  };

  const parseFile = (text) => {
    if (text.charCodeAt(0) === 0xFEFF) text = text.substring(1);
    const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').filter(l => l.trim());
    if (lines.length < 2) return [];

    // Detect separator: try tab first, then semicolon, then comma
    let sep = '\t';
    if (!lines[0].includes('\t')) {
      sep = lines[0].includes(';') ? ';' : ',';
    }

    const headers = lines[0].split(sep).map(h => h.trim().replace(/^["']|["']$/g, ''));

    // Find columns by header name (robust matching)
    const dateIdx = findColumn(headers, 'Date', 'date_operation', 'date operation');
    const libelleIdx = findColumn(headers, 'Libellé', 'Libelle', 'libelle', 'Description', 'Intitulé');
    const catIdx = findColumn(headers, 'Catégorie', 'Categorie', 'categorie', 'Category');
    const montantIdx = findColumn(headers, 'Montant', 'montant', 'Amount', 'Solde');

    // Fallback: use positional if headers not found (old behavior)
    const useDateIdx = dateIdx >= 0 ? dateIdx : 0;
    const useLibelleIdx = libelleIdx >= 0 ? libelleIdx : 1;
    const useCatIdx = catIdx >= 0 ? catIdx : 2;
    const useMontantIdx = montantIdx >= 0 ? montantIdx : 3;

    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const vals = lines[i].split(sep).map(v => v.trim().replace(/^["']|["']$/g, ''));
      if (vals.length < 2) continue;

      const date = vals[useDateIdx] || '';
      const montant = parseMontant(vals[useMontantIdx]);

      // Skip rows without date or with zero montant
      if (!date) continue;

      rows.push({
        date,
        libelle: vals[useLibelleIdx] || '',
        categorie: vals[useCatIdx] || '',
        montant,
      });
    }
    return rows;
  };

  const handleFilePreview = (file) => {
    if (!file) return;
    setSelectedFile(file);
    setPreviewData(null);
    const reader = new FileReader();
    reader.onerror = () => {
      toast.error(`Erreur lecture: ${reader.error?.message || 'fichier illisible'}`);
    };
    reader.onload = (e) => {
      try {
        const text = decodeBuffer(e.target.result);
        if (!text || text.length < 10) {
          toast.error('Fichier vide ou illisible. Essayez "Import direct".');
          return;
        }
        const rows = parseFile(text);
        if (!rows.length) {
          toast.error('Aucune ligne détectée. Le format du fichier n\'est peut-être pas reconnu. Essayez "Import direct".');
          return;
        }
        setPreviewData(rows);
        toast.success(`${rows.length} lignes détectées`);
      } catch (err) {
        console.error('Parse error:', err);
        toast.error(`Erreur de parsing: ${err.message}. Essayez "Import direct" qui envoie le fichier au serveur.`);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleImport = async () => {
    if (!selectedFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const res = await api.post('/linxo-import', formData);
      toast.success(res.data.message);
      setPreviewData(null);
      setSelectedFile(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur import');
    } finally {
      setUploading(false);
    }
  };

  const handleDirectImport = (file) => {
    if (!file) return;
    setSelectedFile(file);
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    api.post('/linxo-import', formData)
      .then(res => { toast.success(res.data.message); setSelectedFile(null); fetchData(); })
      .catch(err => toast.error(err.response?.data?.detail || 'Erreur'))
      .finally(() => setUploading(false));
  };

  const handleDeleteOne = async (id) => {
    try {
      await api.delete(`/cashflow/${id}`);
      toast.success('Supprimé');
      selectedIds.delete(id);
      setSelectedIds(new Set(selectedIds));
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  const handleDeleteBulk = async () => {
    if (selectedIds.size === 0) return;
    setDeleting(true);
    try {
      const res = await api.post('/cashflow/delete-bulk', { ids: [...selectedIds] });
      toast.success(res.data.message);
      setSelectedIds(new Set());
      fetchData();
    } catch { toast.error('Erreur'); }
    finally { setDeleting(false); }
  };

  const handleDeleteAll = async () => {
    setDeleting(true);
    try {
      const res = await api.delete('/cashflow-all');
      toast.success(res.data.message);
      setSelectedIds(new Set());
      fetchData();
    } catch { toast.error('Erreur'); }
    finally { setDeleting(false); }
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === cashflow.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(cashflow.map(c => c.id)));
  };

  // ============ MAPPING ============
  const handleSaveMapping = async (mapping) => {
    try {
      await api.post('/mapping-linxo', mapping);
      // Appliquer immédiatement aux transactions existantes
      const res = await api.post('/cashflow/appliquer-mappings');
      toast.success(`Mapping "${mapping.categorie_linxo}" sauvegardé et appliqué (${res.data.updated + res.data.depenses_updated} lignes)`);
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDeleteMapping = async (id) => {
    try {
      await api.delete(`/mapping-linxo/${id}`);
      toast.success('Supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleAppliquerMappings = async () => {
    setApplying(true);
    try {
      const res = await api.post('/cashflow/appliquer-mappings');
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Erreur application mappings');
    } finally {
      setApplying(false);
    }
  };

  // ============ STATS ============
  const categorisedCount = cashflow.filter(c => c.categorie_interne !== 'A Catégoriser').length;
  const nonCatCount = cashflow.length - categorisedCount;
  const totalNonCat = nonCatGroupes.reduce((s, g) => s + g.count, 0);
  const mappingsMap = Object.fromEntries(mappings.map(m => [m.categorie_linxo, m]));
  const nonCatWithMapping = nonCatGroupes.filter(g => mappingsMap[g.categorie_linxo]);
  const nonCatSansMapping = nonCatGroupes.filter(g => !mappingsMap[g.categorie_linxo]);
  const progress = cashflow.length > 0 ? (categorisedCount / cashflow.length) * 100 : 0;

  // ============ INLINE MAPPING ROW ============
  const InlineMappingRow = ({ groupe }) => {
    const existing = mappingsMap[groupe.categorie_linxo];
    const [form, setForm] = useState({
      categorie_linxo: groupe.categorie_linxo,
      categorie_interne: existing?.categorie_interne || '',
      sous_categorie: existing?.sous_categorie || '',
      type: existing?.type || 'Dépense',
      taux_tva: existing?.taux_tva ?? 0.2,
    });

    const onSave = () => {
      if (!form.categorie_interne) { toast.error('Choisissez une catégorie interne'); return; }
      handleSaveMapping(form);
    };

    return (
      <TableRow className={existing ? 'bg-green-50/50 dark:bg-green-950/10' : 'bg-amber-50/50 dark:bg-amber-950/10'}>
        <TableCell>
          <div>
            <span className="font-medium">{groupe.categorie_linxo}</span>
            <div className="text-xs text-muted-foreground mt-0.5">
              {groupe.exemples.map((ex, i) => (
                <span key={i}>{i > 0 && ' • '}{ex.libelle.substring(0, 30)}</span>
              ))}
            </div>
          </div>
        </TableCell>
        <TableCell className="text-center">
          <Badge variant="secondary">{groupe.count}</Badge>
        </TableCell>
        <TableCell className="text-right tabular-nums font-medium text-red-600">
          {formatCurrency(groupe.total)}
        </TableCell>
        <TableCell>
          <ArrowRight className="w-4 h-4 text-muted-foreground mx-auto" />
        </TableCell>
        <TableCell>
          <Select value={form.categorie_interne} onValueChange={v => setForm(f => ({ ...f, categorie_interne: v }))}>
            <SelectTrigger className="h-8 text-xs" data-testid={`cat-select-${groupe.categorie_linxo}`}>
              <SelectValue placeholder="Catégorie..." />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES_INTERNES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </TableCell>
        <TableCell>
          <Select value={form.sous_categorie} onValueChange={v => setForm(f => ({ ...f, sous_categorie: v }))}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Sous-cat..." />
            </SelectTrigger>
            <SelectContent>
              {SOUS_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </TableCell>
        <TableCell>
          <Select value={form.type} onValueChange={v => setForm(f => ({ ...f, type: v }))}>
            <SelectTrigger className="h-8 text-xs w-[110px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Dépense">Dépense</SelectItem>
              <SelectItem value="Encaissement">Encaissement</SelectItem>
            </SelectContent>
          </Select>
        </TableCell>
        <TableCell>
          <Select value={form.taux_tva.toString()} onValueChange={v => setForm(f => ({ ...f, taux_tva: parseFloat(v) }))}>
            <SelectTrigger className="h-8 text-xs w-[70px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">0%</SelectItem>
              <SelectItem value="0.055">5.5%</SelectItem>
              <SelectItem value="0.1">10%</SelectItem>
              <SelectItem value="0.2">20%</SelectItem>
            </SelectContent>
          </Select>
        </TableCell>
        <TableCell>
          {existing ? (
            <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 text-xs gap-1">
              <CheckCircle className="w-3 h-3" /> Mappé
            </Badge>
          ) : (
            <Button size="sm" className="h-7 text-xs gap-1" onClick={onSave}>
              <Save className="w-3 h-3" /> Mapper
            </Button>
          )}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div className="space-y-6">
      <div className="module-header">
        <div>
          <h1 className="module-title" data-testid="linxo-title">Import Banque (Linxo)</h1>
          <p className="text-muted-foreground mt-1">Importez, catégorisez et envoyez vers les dépenses</p>
        </div>
        <Button variant="outline" onClick={fetchData} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Actualiser
        </Button>
      </div>

      {/* Progress bar */}
      {cashflow.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Progression du mapping</span>
              <span className="text-sm text-muted-foreground">
                {categorisedCount}/{cashflow.length} catégorisées ({progress.toFixed(0)}%)
              </span>
            </div>
            <Progress value={progress} className="h-3" />
            <div className="flex gap-6 mt-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-600" /> {categorisedCount} catégorisées</span>
              <span className="flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-amber-600" /> {nonCatCount} à catégoriser</span>
              <span>{mappings.length} règles de mapping</span>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue={cashflow.length === 0 ? 'import' : (nonCatCount > 0 ? 'categoriser' : 'historique')}>
        <TabsList>
          <TabsTrigger value="import" data-testid="tab-import">
            <Upload className="w-4 h-4 mr-1" /> Import
          </TabsTrigger>
          <TabsTrigger value="categoriser" data-testid="tab-categoriser">
            <AlertTriangle className="w-4 h-4 mr-1" />
            Catégoriser {nonCatCount > 0 && <Badge variant="destructive" className="ml-1 text-xs">{nonCatCount}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="historique" data-testid="tab-historique">
            Historique ({cashflow.length})
          </TabsTrigger>
          <TabsTrigger value="mappings" data-testid="tab-mappings">
            <Settings2 className="w-4 h-4 mr-1" />
            Règles Mapping ({mappings.length})
          </TabsTrigger>
          <TabsTrigger value="verif-plateformes" data-testid="tab-verif-plateformes">
            <CheckCircle className="w-4 h-4 mr-1" />
            Vérif. Plateformes
          </TabsTrigger>
        </TabsList>

        {/* ====== TAB IMPORT ====== */}
        <TabsContent value="import" className="space-y-6">
          <Card>
            <CardContent className="p-8">
              <div
                className={`border-2 border-dashed rounded-xl p-12 text-center transition-all ${dragActive ? 'border-primary bg-primary/5' : 'border-border'}`}
                onDragEnter={e => { e.preventDefault(); setDragActive(true); }}
                onDragLeave={e => { e.preventDefault(); setDragActive(false); }}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); setDragActive(false); if (e.dataTransfer.files[0]) handleFilePreview(e.dataTransfer.files[0]); }}
              >
                <div className="flex flex-col items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                    <Upload className="w-8 h-8 text-primary" />
                  </div>
                  <div>
                    <p className="text-lg font-medium">Glissez votre fichier Linxo ici</p>
                    <p className="text-muted-foreground mt-1">Format: Date, Libellé, Catégorie, Montant (CSV/TSV, UTF-8 ou UTF-16)</p>
                  </div>
                  <input type="file" accept=".csv,.tsv,.txt,.xls,.xlsx,.ods" className="hidden" id="file-upload"
                    onChange={e => { if (e.target.files[0]) handleFilePreview(e.target.files[0]); e.target.value = ''; }} />
                  <input type="file" accept=".csv,.tsv,.txt,.xls,.xlsx,.ods" className="hidden" id="file-direct"
                    onChange={e => { if (e.target.files[0]) handleDirectImport(e.target.files[0]); e.target.value = ''; }} />
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => document.getElementById('file-upload').click()} data-testid="select-file-btn">
                      <FileSpreadsheet className="w-4 h-4 mr-2" /> Aperçu du fichier
                    </Button>
                    <Button disabled={uploading} onClick={() => document.getElementById('file-direct').click()} data-testid="direct-import-btn">
                      <Upload className="w-4 h-4 mr-2" /> {uploading ? 'Import...' : 'Import direct'}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {previewData && previewData.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle data-testid="preview-title">Aperçu ({previewData.length} lignes){selectedFile && <span className="text-sm font-normal text-muted-foreground ml-2">— {selectedFile.name}</span>}</CardTitle>
                    <CardDescription>{[...new Set(previewData.map(r => r.categorie).filter(Boolean))].length} catégories détectées</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => { setPreviewData(null); setSelectedFile(null); }}>Annuler</Button>
                    <Button onClick={handleImport} disabled={uploading} data-testid="confirm-import-btn">
                      {uploading ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Import...</> : `Importer ${previewData.length} lignes`}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[350px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Libellé</TableHead>
                        <TableHead>Catégorie banque</TableHead>
                        <TableHead className="text-right">Montant</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {previewData.slice(0, 50).map((row, i) => (
                        <TableRow key={i}>
                          <TableCell>{row.date}</TableCell>
                          <TableCell className="max-w-[250px] truncate">{row.libelle}</TableCell>
                          <TableCell><Badge variant="outline">{row.categorie}</Badge></TableCell>
                          <TableCell className={`text-right tabular-nums font-medium ${row.montant < 0 ? 'text-red-600' : 'text-green-600'}`}>{formatCurrency(row.montant)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {previewData.length > 50 && <div className="p-4 text-center text-muted-foreground">+ {previewData.length - 50} autres lignes</div>}
                </ScrollArea>
              </CardContent>
            </Card>
          )}

          {/* Flow explanation */}
          <Card className="bg-muted/30">
            <CardContent className="pt-6">
              <h3 className="font-medium mb-3">Comment ça marche ?</h3>
              <div className="flex items-start gap-8">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">1</div>
                  <div>
                    <p className="text-sm font-medium">Importer</p>
                    <p className="text-xs text-muted-foreground">Uploadez votre fichier Linxo</p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 mt-1.5 text-muted-foreground shrink-0" />
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-amber-500 text-white flex items-center justify-center text-sm font-bold">2</div>
                  <div>
                    <p className="text-sm font-medium">Catégoriser</p>
                    <p className="text-xs text-muted-foreground">Mappez chaque catégorie banque vers une catégorie interne</p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 mt-1.5 text-muted-foreground shrink-0" />
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center text-sm font-bold">3</div>
                  <div>
                    <p className="text-sm font-medium">Appliquer</p>
                    <p className="text-xs text-muted-foreground">Les dépenses sont créées automatiquement dans le module Dépenses</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ====== TAB CATEGORISER ====== */}
        <TabsContent value="categoriser" className="space-y-4">
          {nonCatGroupes.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
                <p className="text-lg font-medium">Toutes les transactions sont catégorisées !</p>
                <p className="text-muted-foreground mt-1">Importez un nouveau fichier ou consultez l'historique.</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Action bar */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">{totalNonCat} transactions à catégoriser</h3>
                  <p className="text-sm text-muted-foreground">
                    {nonCatSansMapping.length} catégorie(s) sans mapping •{' '}
                    {nonCatWithMapping.length} catégorie(s) avec mapping prêtes à appliquer
                  </p>
                </div>
                {nonCatWithMapping.length > 0 && (
                  <Button
                    onClick={handleAppliquerMappings}
                    disabled={applying}
                    className="gap-2"
                    data-testid="appliquer-mappings-btn"
                  >
                    <PlayCircle className="w-4 h-4" />
                    {applying ? 'Application...' : `Appliquer les mappings (${nonCatWithMapping.reduce((s, g) => s + g.count, 0)} lignes)`}
                  </Button>
                )}
              </div>

              {/* Catégories sans mapping d'abord */}
              {nonCatSansMapping.length > 0 && (
                <Card className="border-amber-300">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2 text-amber-700 dark:text-amber-400">
                      <AlertTriangle className="w-4 h-4" />
                      Catégories sans mapping ({nonCatSansMapping.length})
                    </CardTitle>
                    <CardDescription>Définissez un mapping pour chaque catégorie banque, puis cliquez "Appliquer"</CardDescription>
                  </CardHeader>
                  <CardContent className="p-0 overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="min-w-[200px]">Catégorie banque</TableHead>
                            <TableHead className="text-center w-[60px]">Nb</TableHead>
                            <TableHead className="text-right w-[100px]">Total</TableHead>
                            <TableHead className="w-[30px]"></TableHead>
                            <TableHead>Catégorie interne</TableHead>
                            <TableHead>Sous-catégorie</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>TVA</TableHead>
                            <TableHead className="w-[90px]"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {nonCatSansMapping.map(g => <InlineMappingRow key={g.categorie_linxo} groupe={g} />)}
                        </TableBody>
                      </Table>
                  </CardContent>
                </Card>
              )}

              {/* Catégories avec mapping */}
              {nonCatWithMapping.length > 0 && (
                <Card className="border-green-300">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2 text-green-700 dark:text-green-400">
                      <CheckCircle className="w-4 h-4" />
                      Mappées — prêtes à appliquer ({nonCatWithMapping.length})
                    </CardTitle>
                    <CardDescription>Cliquez "Appliquer les mappings" pour catégoriser et créer les dépenses</CardDescription>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Catégorie banque</TableHead>
                          <TableHead className="text-center">Nb</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                          <TableHead></TableHead>
                          <TableHead>Catégorie interne</TableHead>
                          <TableHead>Sous-catégorie</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>TVA</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {nonCatWithMapping.map(g => <InlineMappingRow key={g.categorie_linxo} groupe={g} />)}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* Existing mappings list */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Règles de mapping ({mappings.length})</CardTitle>
              <CardDescription>Ces règles s'appliquent aussi aux futurs imports</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {mappings.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground">Aucun mapping. Créez-en ci-dessus.</div>
              ) : (
                <ScrollArea className="max-h-[300px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Catégorie banque</TableHead>
                        <TableHead></TableHead>
                        <TableHead>Catégorie interne</TableHead>
                        <TableHead>Sous-cat.</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>TVA</TableHead>
                        <TableHead className="w-[50px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {mappings.map(m => (
                        <TableRow key={m.id}>
                          <TableCell className="font-medium">{m.categorie_linxo}</TableCell>
                          <TableCell><ArrowRight className="w-4 h-4 text-muted-foreground" /></TableCell>
                          <TableCell>{m.categorie_interne}</TableCell>
                          <TableCell className="text-muted-foreground">{m.sous_categorie}</TableCell>
                          <TableCell><Badge variant={m.type === 'Encaissement' ? 'default' : 'secondary'}>{m.type}</Badge></TableCell>
                          <TableCell>{(m.taux_tva * 100).toFixed(0)}%</TableCell>
                          <TableCell>
                            <Button variant="ghost" size="icon" className="text-destructive h-7 w-7" onClick={() => handleDeleteMapping(m.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ====== TAB HISTORIQUE ====== */}
        <TabsContent value="historique">
          <HistoriqueTab
            cashflow={cashflow}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            deleting={deleting}
            handleDeleteBulk={handleDeleteBulk}
            handleDeleteAll={handleDeleteAll}
            handleDeleteOne={handleDeleteOne}
            toggleSelect={toggleSelect}
            toggleSelectAll={toggleSelectAll}
            filterMois={filterMois} setFilterMois={setFilterMois}
            filterLibelle={filterLibelle} setFilterLibelle={setFilterLibelle}
            filterCatBanque={filterCatBanque} setFilterCatBanque={setFilterCatBanque}
            filterCatInterne={filterCatInterne} setFilterCatInterne={setFilterCatInterne}
            filterType={filterType} setFilterType={setFilterType}
            api={api} onRefresh={fetchData}
            categories={CATEGORIES_INTERNES} sousCategories={SOUS_CATEGORIES}
          />
        </TabsContent>

        {/* ====== TAB MAPPINGS ====== */}
        <TabsContent value="mappings" className="space-y-4">
          <MappingManager mappings={mappings} api={api} onRefresh={fetchData} categories={CATEGORIES_INTERNES} sousCategories={SOUS_CATEGORIES} />
        </TabsContent>

        {/* ====== TAB VERIF PLATEFORMES ====== */}
        <TabsContent value="verif-plateformes" className="space-y-4">
          <VerificationPlateformes api={api} />
        </TabsContent>
      </Tabs>
    </div>
  );
}


function MappingManager({ mappings, api, onRefresh, categories, sousCategories }) {
  const [selected, setSelected] = useState(new Set());
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [deleting, setDeleting] = useState(false);

  const toggleSelect = (id) => {
    setSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };
  const toggleAll = () => {
    setSelected(prev => prev.size === mappings.length ? new Set() : new Set(mappings.map(m => m.id)));
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    setDeleting(true);
    try {
      await api.post('/mapping-linxo/delete-bulk', { ids: [...selected] });
      toast.success(`${selected.size} règle(s) supprimée(s)`);
      setSelected(new Set());
      onRefresh();
    } catch { toast.error('Erreur suppression'); }
    finally { setDeleting(false); }
  };

  const handleDeleteAll = async () => {
    setDeleting(true);
    try {
      const res = await api.post('/mapping-linxo/delete-all');
      toast.success(res.data.message);
      setSelected(new Set());
      onRefresh();
    } catch { toast.error('Erreur suppression'); }
    finally { setDeleting(false); }
  };

  const handleDeleteOne = async (id) => {
    try {
      await api.delete(`/mapping-linxo/${id}`);
      toast.success('Règle supprimée');
      onRefresh();
    } catch { toast.error('Erreur'); }
  };

  const startEdit = (m) => {
    setEditingId(m.id);
    setEditForm({ categorie_interne: m.categorie_interne, sous_categorie: m.sous_categorie || '', type: m.type, taux_tva: m.taux_tva });
  };

  const saveEdit = async (m) => {
    try {
      await api.put(`/mapping-linxo/${m.id}`, { categorie_linxo: m.categorie_linxo, ...editForm });
      await api.post('/cashflow/appliquer-mappings');
      toast.success('Mapping mis à jour et appliqué');
      setEditingId(null);
      onRefresh();
    } catch { toast.error('Erreur mise à jour'); }
  };

  return (
    <div className="space-y-4">
      {/* Actions */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium">{mappings.length} règle(s) de mapping</span>
              {selected.size > 0 && (
                <Badge variant="destructive">{selected.size} sélectionnée(s)</Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              {selected.size > 0 && (
                <Button variant="destructive" size="sm" onClick={handleBulkDelete} disabled={deleting} className="gap-1.5" data-testid="bulk-delete-mappings">
                  <Trash2 className="w-4 h-4" /> Supprimer ({selected.size})
                </Button>
              )}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/10" data-testid="delete-all-mappings">
                    <Trash2 className="w-4 h-4" /> Tout supprimer
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Supprimer toutes les règles de mapping ?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Cette action va supprimer les {mappings.length} règle(s) de mapping. Les transactions déjà catégorisées ne seront pas modifiées. Vous devrez recatégoriser les nouvelles transactions.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDeleteAll} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Supprimer tout</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {mappings.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Settings2 className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="font-medium">Aucune règle de mapping</p>
            <p className="text-sm mt-1">Allez dans l'onglet "Catégoriser" pour créer des règles</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <ScrollArea className="max-h-[60vh]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox checked={selected.size === mappings.length && mappings.length > 0} onCheckedChange={toggleAll} data-testid="select-all-mappings" />
                    </TableHead>
                    <TableHead>Catégorie Linxo</TableHead>
                    <TableHead>Catégorie Interne</TableHead>
                    <TableHead>Sous-catégorie</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>TVA</TableHead>
                    <TableHead className="w-24">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mappings.map(m => (
                    <TableRow key={m.id} className={selected.has(m.id) ? 'bg-destructive/5' : ''}>
                      <TableCell>
                        <Checkbox checked={selected.has(m.id)} onCheckedChange={() => toggleSelect(m.id)} />
                      </TableCell>
                      <TableCell className="font-medium">{m.categorie_linxo}</TableCell>
                      {editingId === m.id ? (
                        <>
                          <TableCell>
                            <Select value={editForm.categorie_interne} onValueChange={v => setEditForm(f => ({ ...f, categorie_interne: v }))}>
                              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Select value={editForm.sous_categorie} onValueChange={v => setEditForm(f => ({ ...f, sous_categorie: v }))}>
                              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="..." /></SelectTrigger>
                              <SelectContent>{sousCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Select value={editForm.type} onValueChange={v => setEditForm(f => ({ ...f, type: v }))}>
                              <SelectTrigger className="h-8 text-xs w-[110px]"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Dépense">Dépense</SelectItem>
                                <SelectItem value="Encaissement">Encaissement</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Select value={editForm.taux_tva.toString()} onValueChange={v => setEditForm(f => ({ ...f, taux_tva: parseFloat(v) }))}>
                              <SelectTrigger className="h-8 text-xs w-[70px]"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="0">0%</SelectItem>
                                <SelectItem value="0.055">5.5%</SelectItem>
                                <SelectItem value="0.1">10%</SelectItem>
                                <SelectItem value="0.2">20%</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button size="icon" className="h-7 w-7" onClick={() => saveEdit(m)} data-testid={`save-mapping-${m.id}`}>
                                <Save className="w-3.5 h-3.5" />
                              </Button>
                              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingId(null)}>
                                <ArrowRight className="w-3.5 h-3.5 rotate-180" />
                              </Button>
                            </div>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>
                            <Badge variant="secondary">{m.categorie_interne}</Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">{m.sous_categorie || '-'}</TableCell>
                          <TableCell>
                            <Badge variant={m.type === 'Dépense' ? 'destructive' : 'default'} className="text-xs">{m.type}</Badge>
                          </TableCell>
                          <TableCell className="text-sm">{(m.taux_tva * 100).toFixed(1)}%</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => startEdit(m)} data-testid={`edit-mapping-${m.id}`}>
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDeleteOne(m.id)} data-testid={`delete-mapping-${m.id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


const MOIS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];

function HistoriqueTab({ cashflow, selectedIds, setSelectedIds, deleting, handleDeleteBulk, handleDeleteAll, handleDeleteOne, toggleSelect, toggleSelectAll, filterMois, setFilterMois, filterLibelle, setFilterLibelle, filterCatBanque, setFilterCatBanque, filterCatInterne, setFilterCatInterne, filterType, setFilterType, api, onRefresh, categories, sousCategories }) {
  const [editingId, setEditingId] = useState(null);
  const [editCat, setEditCat] = useState('');
  const [editSousCat, setEditSousCat] = useState('');
  const [editType, setEditType] = useState('Dépense');
  const [editTva, setEditTva] = useState('0');
  const [saving, setSaving] = useState(false);

  // Extract unique values for dropdowns
  const uniqueMois = [...new Set(cashflow.map(c => {
    if (!c.date) return null;
    const d = new Date(c.date);
    if (isNaN(d)) return null;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }).filter(Boolean))].sort().reverse();

  const uniqueCatBanque = [...new Set(cashflow.map(c => c.categorie_linxo).filter(Boolean))].sort();
  const uniqueCatInterne = [...new Set(cashflow.map(c => c.categorie_interne).filter(Boolean))].sort();
  const uniqueTypes = [...new Set(cashflow.map(c => c.type2).filter(Boolean))].sort();

  // Filter
  const filtered = cashflow.filter(item => {
    if (filterMois !== 'all' && item.date) {
      const d = new Date(item.date);
      const moisKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (moisKey !== filterMois) return false;
    }
    if (filterLibelle && !item.libelle_linxo?.toLowerCase().includes(filterLibelle.toLowerCase())) return false;
    if (filterCatBanque !== 'all' && item.categorie_linxo !== filterCatBanque) return false;
    if (filterCatInterne !== 'all' && item.categorie_interne !== filterCatInterne) return false;
    if (filterType !== 'all' && item.type2 !== filterType) return false;
    return true;
  });

  const totalFiltered = filtered.reduce((s, c) => s + (c.montant_ttc || 0), 0);
  const hasFilters = filterMois !== 'all' || filterLibelle || filterCatBanque !== 'all' || filterCatInterne !== 'all' || filterType !== 'all';

  const resetFilters = () => {
    setFilterMois('all');
    setFilterLibelle('');
    setFilterCatBanque('all');
    setFilterCatInterne('all');
    setFilterType('all');
  };

  const startCategorize = (item) => {
    setEditingId(item.id);
    setEditCat(item.categorie_interne === 'A Catégoriser' ? '' : item.categorie_interne);
    setEditSousCat(item.sous_categorie || '__none__');
    setEditType(item.type2 || 'Dépense');
    setEditTva('0');
  };

  const saveCategorize = async (itemId) => {
    if (!editCat) { toast.error('Sélectionnez une catégorie'); return; }
    setSaving(true);
    try {
      await api.put(`/cashflow/${itemId}/categorize`, {
        categorie_interne: editCat,
        sous_categorie: editSousCat === '__none__' ? '' : editSousCat,
        type: editType,
        taux_tva: parseFloat(editTva),
      });
      toast.success('Transaction catégorisée');
      setEditingId(null);
      onRefresh();
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const formatMoisLabel = (key) => {
    const [y, m] = key.split('-');
    return `${MOIS_FR[parseInt(m) - 1]} ${y}`;
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">
            Transactions {hasFilters ? `(${filtered.length}/${cashflow.length})` : `(${cashflow.length})`}
          </CardTitle>
          <div className="flex items-center gap-2">
            {selectedIds.size > 0 && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm" className="gap-2" data-testid="delete-selected-btn" disabled={deleting}>
                    {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    Supprimer ({selectedIds.size})
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
                    <AlertDialogDescription>Supprimer les {selectedIds.size} ligne(s) sélectionnée(s) ?</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDeleteBulk} data-testid="confirm-delete-bulk">Supprimer</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            {cashflow.length > 0 && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2 text-destructive border-destructive/50 hover:bg-destructive/10" data-testid="delete-all-btn" disabled={deleting}>
                    <Trash2 className="w-4 h-4" /> Tout supprimer
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Supprimer toutes les données ?</AlertDialogTitle>
                    <AlertDialogDescription>Cela supprimera les {cashflow.length} transactions. Cette action est irréversible.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDeleteAll} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-all">Tout supprimer</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
      </CardHeader>

      {/* Filters */}
      <div className="px-6 pb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[160px]">
            <Label className="text-xs text-muted-foreground mb-1 block">Mois</Label>
            <Select value={filterMois} onValueChange={setFilterMois}>
              <SelectTrigger className="h-8 text-xs" data-testid="filter-mois">
                <SelectValue placeholder="Tous" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les mois</SelectItem>
                {uniqueMois.map(m => (
                  <SelectItem key={m} value={m}>{formatMoisLabel(m)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[160px]">
            <Label className="text-xs text-muted-foreground mb-1 block">Libellé</Label>
            <Input value={filterLibelle} onChange={e => setFilterLibelle(e.target.value)} placeholder="Rechercher..." className="h-8 text-xs" data-testid="filter-libelle" />
          </div>
          <div className="min-w-[160px]">
            <Label className="text-xs text-muted-foreground mb-1 block">Cat. banque</Label>
            <Select value={filterCatBanque} onValueChange={setFilterCatBanque}>
              <SelectTrigger className="h-8 text-xs" data-testid="filter-cat-banque">
                <SelectValue placeholder="Toutes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                {uniqueCatBanque.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[160px]">
            <Label className="text-xs text-muted-foreground mb-1 block">Cat. interne</Label>
            <Select value={filterCatInterne} onValueChange={setFilterCatInterne}>
              <SelectTrigger className="h-8 text-xs" data-testid="filter-cat-interne">
                <SelectValue placeholder="Toutes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                {uniqueCatInterne.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[130px]">
            <Label className="text-xs text-muted-foreground mb-1 block">Type</Label>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="h-8 text-xs" data-testid="filter-type">
                <SelectValue placeholder="Tous" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                {uniqueTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {hasFilters && (
            <Button variant="ghost" size="sm" className="h-8 text-xs gap-1" onClick={resetFilters} data-testid="reset-filters">
              Réinitialiser
            </Button>
          )}
        </div>
        {hasFilters && (
          <div className="mt-2 text-xs text-muted-foreground">
            {filtered.length} transaction(s) affichée(s) — Total : <span className={`font-semibold ${totalFiltered < 0 ? 'text-red-600' : 'text-green-600'}`}>{formatCurrency(totalFiltered)}</span>
          </div>
        )}
      </div>

      <CardContent className="p-0">
        <ScrollArea className="h-[500px]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]">
                  <Checkbox
                    checked={filtered.length > 0 && selectedIds.size === filtered.length}
                    onCheckedChange={() => {
                      if (selectedIds.size === filtered.length) setSelectedIds(new Set());
                      else setSelectedIds(new Set(filtered.map(i => i.id)));
                    }}
                    data-testid="select-all-cashflow"
                  />
                </TableHead>
                <TableHead>Mois</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Libellé</TableHead>
                <TableHead>Cat. banque</TableHead>
                <TableHead>Cat. interne</TableHead>
                <TableHead className="text-right">Montant</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>TVA</TableHead>
                <TableHead className="w-[70px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    {cashflow.length === 0 ? 'Aucune donnée importée' : 'Aucun résultat avec ces filtres'}
                  </TableCell>
                </TableRow>
              ) : filtered.map(item => {
                const d = item.date ? new Date(item.date) : null;
                const moisLabel = d ? `${MOIS_FR[d.getMonth()].substring(0, 3)} ${d.getFullYear()}` : '-';
                const isEditing = editingId === item.id;
                return (
                  <TableRow key={item.id} className={selectedIds.has(item.id) ? 'bg-primary/5' : isEditing ? 'bg-blue-50/50' : ''}>
                    <TableCell>
                      <Checkbox checked={selectedIds.has(item.id)} onCheckedChange={() => toggleSelect(item.id)} />
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{moisLabel}</TableCell>
                    <TableCell className="whitespace-nowrap">{formatDate(item.date)}</TableCell>
                    <TableCell className="max-w-[200px] truncate" title={item.libelle_linxo}>{item.libelle_linxo}</TableCell>
                    <TableCell className="text-muted-foreground text-xs">{item.categorie_linxo}</TableCell>
                    {isEditing ? (
                      <>
                        <TableCell>
                          <Select value={editCat} onValueChange={setEditCat}>
                            <SelectTrigger className="h-7 text-xs w-[140px]"><SelectValue placeholder="Catégorie..." /></SelectTrigger>
                            <SelectContent>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Select value={editSousCat} onValueChange={setEditSousCat}>
                            <SelectTrigger className="h-7 text-xs w-[110px]"><SelectValue placeholder="..." /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">Aucune</SelectItem>
                              {sousCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Select value={editType} onValueChange={setEditType}>
                            <SelectTrigger className="h-7 text-xs w-[100px]"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Dépense">Dépense</SelectItem>
                              <SelectItem value="Encaissement">Encaissement</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Select value={editTva} onValueChange={setEditTva}>
                            <SelectTrigger className="h-7 text-xs w-[65px]"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="0">0%</SelectItem>
                              <SelectItem value="0.055">5.5%</SelectItem>
                              <SelectItem value="0.1">10%</SelectItem>
                              <SelectItem value="0.2">20%</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button size="icon" className="h-7 w-7" onClick={() => saveCategorize(item.id)} disabled={saving}>
                              <Save className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingId(null)}>
                              <RefreshCw className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>
                          {item.categorie_interne === 'A Catégoriser' ? (
                            <Badge variant="outline" className="bg-amber-100 text-amber-800 text-xs cursor-pointer hover:bg-amber-200" onClick={() => startCategorize(item)}>A Catégoriser</Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 text-xs cursor-pointer hover:opacity-80" onClick={() => startCategorize(item)}>{item.categorie_interne}</Badge>
                          )}
                        </TableCell>
                        <TableCell className={`text-right tabular-nums font-medium ${item.montant_ttc < 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {formatCurrency(item.montant_ttc)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={item.type2 === 'Dépense' ? 'destructive' : 'default'} className="text-xs">{item.type2}</Badge>
                        </TableCell>
                        <TableCell></TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-600 hover:text-blue-700" onClick={() => startCategorize(item)} data-testid={`categorize-${item.id}`}>
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDeleteOne(item.id)}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}


function VerificationPlateformes({ api }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());

  useEffect(() => {
    const fetchVerif = async () => {
      setLoading(true);
      try {
        const res = await api.get('/cashflow/verification-plateformes', { params: { annee: year } });
        setData(res.data);
      } catch {
        setData([]);
      }
      setLoading(false);
    };
    fetchVerif();
  }, [api, year]);

  const moisNom = (m) => ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'][m] || m;

  if (loading) return <Card><CardContent className="py-12 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />Chargement...</CardContent></Card>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium text-lg">Vérification Encaissements Plateformes</h3>
          <p className="text-sm text-muted-foreground">Comparaison entre les encaissements Linxo (banque) et les saisies manuelles dans Revenus Plateformes</p>
        </div>
        <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
          <SelectTrigger className="w-[120px]" data-testid="verif-year">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[2024, 2025, 2026, 2027].map(y => (
              <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {data.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Aucun encaissement plateforme trouvé dans les données Linxo pour {year}.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Plateforme</TableHead>
                  <TableHead>Mois</TableHead>
                  <TableHead className="text-right">Encaissements Linxo</TableHead>
                  <TableHead className="text-center">Nb Linxo</TableHead>
                  <TableHead className="text-right">Revenus Plateformes (saisie)</TableHead>
                  <TableHead className="text-center">Nb saisies</TableHead>
                  <TableHead className="text-right">Écart</TableHead>
                  <TableHead>Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((row, i) => (
                  <TableRow key={i} className={!row.conforme ? 'bg-amber-50/50 dark:bg-amber-950/10' : ''}>
                    <TableCell className="font-medium">{row.plateforme}</TableCell>
                    <TableCell>{moisNom(row.mois)}</TableCell>
                    <TableCell className="text-right tabular-nums text-green-600 font-medium">{formatCurrency(row.total_linxo)}</TableCell>
                    <TableCell className="text-center"><Badge variant="secondary" className="text-xs">{row.count_linxo}</Badge></TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{formatCurrency(row.total_revenus_plateformes)}</TableCell>
                    <TableCell className="text-center"><Badge variant="secondary" className="text-xs">{row.count_revenus}</Badge></TableCell>
                    <TableCell className={`text-right tabular-nums font-bold ${Math.abs(row.ecart) < 1 ? 'text-green-600' : 'text-red-600'}`}>
                      {row.ecart > 0 ? '+' : ''}{formatCurrency(row.ecart)}
                    </TableCell>
                    <TableCell>
                      {row.conforme ? (
                        <Badge className="bg-green-100 text-green-800 gap-1 text-xs"><CheckCircle className="w-3 h-3" /> OK</Badge>
                      ) : row.count_revenus === 0 ? (
                        <Badge variant="outline" className="text-amber-600 border-amber-300 gap-1 text-xs"><AlertTriangle className="w-3 h-3" /> Non saisi</Badge>
                      ) : (
                        <Badge variant="destructive" className="gap-1 text-xs"><AlertTriangle className="w-3 h-3" /> Écart</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
